import './application.css'
import '../i18n' // Import i18n configuration
import { createRoot } from 'react-dom/client'
import { createInertiaApp } from '@inertiajs/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import axios from 'axios'
import { getCsrfToken } from '@/lib/csrf'

const queryClient = new QueryClient()

// CSRF Token 설정
// 정적 default 헤더는 모듈 로드 시점 토큰으로 고정되어 stale 해진다.
// 매 요청마다 getCsrfToken()(XSRF-TOKEN 쿠키 우선)으로 최신 토큰을 주입한다.
axios.interceptors.request.use((config) => {
  const token = getCsrfToken()
  if (token) {
    config.headers['X-CSRF-Token'] = token
  }
  return config
})

interface PageModule {
  default: React.ComponentType<any>;
  layout?: (page: React.ReactNode) => React.ReactNode;
}

createInertiaApp({
  resolve: async (name) => {
    const pages = import.meta.glob('../Pages/**/*.{tsx,jsx}')
    const directMatch = pages[`../Pages/${name}.tsx`] || pages[`../Pages/${name}.jsx`]

    let match = directMatch
    if (!match) {
      const expectedLower = `../Pages/${name}`.toLowerCase()
      const path = Object.keys(pages).find(
        (path) =>
          path.toLowerCase() === `${expectedLower}.tsx` ||
          path.toLowerCase() === `${expectedLower}.jsx` ||
          path.toLowerCase() === `${expectedLower}/index.tsx` ||
          path.toLowerCase() === `${expectedLower}/index.jsx`
      )
      if (path) {
        match = pages[path]
        console.debug(`[Inertia] Found component ${name} at path ${path}`)
      }
    }

    if (!match) {
      console.error(`[Inertia] Page not found: ${name}. Available pages:`, Object.keys(pages))
      throw new Error(`Inertia page not found: ${name}`)
    }

    const module = await (match as () => Promise<PageModule>)()
    console.debug(`[Inertia] Loaded component: ${name}`, module)
    return module
  },
  setup({ el, App, props }) {
    createRoot(el).render(
      <QueryClientProvider client={queryClient}>
        <App {...props} />
      </QueryClientProvider>
    )
  },
})
